import {
    Dialog,
    DialogContent,
    DialogActions,
    Box,
    Checkbox,
    Typography,
    CircularProgress,
    Divider,
    Tabs,
    Tab
} from "@mui/material";

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { GeneralButton } from "../../common/ui/GeneralButton";
import ServerSideTableColumnHeader from "../../components/common/table/ServerSideTableColumnHeader";

import { useAppDispatch } from "../../hooks/StoreHooks";
import ServerSideSearchBar from "../common/table/ServerSideSearchBar";
import { ServerSidePagination } from "../common/table/ServerSidePagination";
import { useServerSidePagination } from "../../hooks/PaginationHooks";

import { fetchPaginatedEmployeesByIdThunk } from "../../store/thunk/employeesThunk";
import { EmployeeUserType } from "../../types/employeeType";

interface Props {
    onClose: () => void;
    selectedEmployees: string[];
    handleOnSave: (newSelectedEmployeeIds: string[]) => void;
}

export default function SelectEmployeesDialog(props: Props) {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();

    const [isLoadingEmployees, setIsLoadingEmployees] = useState<boolean>(false);
    const [currentTotalIds, setCurrentTotalIds] = useState<string[]>([]);

    // 0 = all, 1 = selected
    const [selectedTab, setSelectedTab] = useState<number>(0);

    const [selectedEmployeeIds, setSelectedEmployeesIds] = useState<Set<string>>(() => {
        return new Set(props.selectedEmployees.map(employeeId => employeeId));
    });

    const paginatedDataEmployees = useServerSidePagination<EmployeeUserType>(async (paginationParameters) => {

        if (selectedTab === 1 && selectedEmployeeIds.size === 0)
        {
            setCurrentTotalIds([]);
            return { items: [], totalCount: 0 };
        }
        
        const paramEmployees = selectedTab === 1 ? Array.from(selectedEmployeeIds) : [] // Empty to get all
        
        setIsLoadingEmployees(true);
        const resultAction = await dispatch(fetchPaginatedEmployeesByIdThunk({paginationParams: paginationParameters, employeeIds: paramEmployees }));
        setIsLoadingEmployees(false);

        if (fetchPaginatedEmployeesByIdThunk.fulfilled.match(resultAction) && resultAction.payload) {
            setCurrentTotalIds(resultAction.payload.totalIds);
            return resultAction.payload; 
        }
        else {
            return { items: [], totalCount: 0};
        } 
    });

    const resetPagination = () => {
        paginatedDataEmployees.handleOnPageChange({
        ...paginatedDataEmployees.paginationParameters,
        offset: 0
        });
}

    return (
        <Dialog
            open={true}
            onClose={props.onClose}
            fullWidth
            maxWidth="md"
            slotProps={{
                paper: { sx: { padding: 2, borderRadius: 3 } },
            }}
        >
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column'}}>
        <Tabs 
            sx={{marginBottom: 2}}
            value={selectedTab}
            onChange={(_, newValue) => {
                setSelectedTab(newValue);
                resetPagination();
            }}
        >
            <Tab label={t("groupsPage.employees.select")} />
            <Tab label={`${t("groupsPage.employees.added")} (${selectedEmployeeIds.size})`} />
        </Tabs>

    <ServerSideSearchBar onSearchHandler={(val) => { paginatedDataEmployees.handleOnSearch(val)}} />

    <Box sx={{
        display: "grid",
        gridTemplateColumns: "2fr 2fr 2fr 1.5fr 50px",
        alignItems: "center",
        gap: 0.5,
        overflowY: "auto",
        mt: 2,
    }}>
        <Box sx={{ display: "contents", '& .MuiTypography-root': { cursor: 'pointer', fontWeight: 'bold' } }}>
            <ServerSideTableColumnHeader label={t("tableColumnHeaders.firstName")} fieldName="firstName" isSorted paginatedData={paginatedDataEmployees} />
            <ServerSideTableColumnHeader label={t("tableColumnHeaders.lastName")} fieldName="lastName" isSorted paginatedData={paginatedDataEmployees} />
            <ServerSideTableColumnHeader label={t("tableColumnHeaders.employeeNo")} fieldName="employeeNumber" isSorted paginatedData={paginatedDataEmployees} />
            <ServerSideTableColumnHeader label={t("tableColumnHeaders.position")} fieldName="position" isSorted paginatedData={paginatedDataEmployees} />
            <Box /> 
        </Box>
        <Divider sx={{ gridColumn: "span 5", my: 1 }} />

        {paginatedDataEmployees.items.map((employee) => (
            <Box key={employee.id} sx={{ display: "contents" }}>
                <Typography noWrap sx={{ pl: 2 }}>{employee.firstName}</Typography>
                <Typography noWrap sx={{ pl: 2 }}>{employee.lastName}</Typography>
                <Typography noWrap sx={{ pl: 2 }}>{employee.employeeNumber}</Typography>
                <Typography noWrap sx={{ pl: 2 }}>{employee.position}</Typography>
                <Checkbox
                    checked={selectedEmployeeIds.has(employee.id)}
                    onChange={(e) => {
                        const newSelected = new Set(selectedEmployeeIds);
                        e.target.checked ? newSelected.add(employee.id) : newSelected.delete(employee.id);
                        setSelectedEmployeesIds(newSelected);
                    }}
                />
            </Box>
        ))}
    </Box>
</DialogContent>

    {isLoadingEmployees && <CircularProgress size={24} />}
            <ServerSidePagination 
                // Reset state by changing key
                key={`pagination-tab-${selectedTab}`} 
                paginatedData={paginatedDataEmployees} 
            />
            <Divider sx={{ my: 2 }} />
            <DialogActions>
                <GeneralButton appVariant="secondary" onClick={() => {
                    const newSelected = new Set(selectedEmployeeIds);
                    currentTotalIds.forEach(id => newSelected.add(id));
                    setSelectedEmployeesIds(newSelected);
                }}>
                    {t("groupsPage.employees.addAll")}
                </GeneralButton>
                <GeneralButton appVariant="secondary" onClick={() => {
                    const newSelected = new Set(selectedEmployeeIds);
                    newSelected.clear();
                    setSelectedEmployeesIds(newSelected);
                }}>
                    {t("groupsPage.employees.removeAll")}
                </GeneralButton>
                <GeneralButton appVariant="secondary" onClick={props.onClose}>
                    {t("buttons.cancel")}
                </GeneralButton>
                <GeneralButton onClick={() => props.handleOnSave(Array.from(selectedEmployeeIds))}>
                    {t("buttons.apply")}
                </GeneralButton>
            </DialogActions>
        </Dialog>
    );
};
